// src/components/dashboard/VacationMetricCard.js

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useVacation } from '../../contexts/VacationContext';
import { useAuth } from '../../contexts/AuthContext';
import MetricCard from './MetricCard';
import { RiSunLine } from 'react-icons/ri';

/**
 * Thin, self-fetching wrapper around the presentational <MetricCard/>.
 * Data path unchanged from the original component: still calls
 * useVacation().fetchVacationsForDay(new Date()) and counts the result.
 *
 * @param {Object} props
 * @param {number} [props.staffTotal] - total staff count, used to render the
 *   "von {staffTotal}" sub-label. Optional so this component still works
 *   standalone (no sub-label) if rendered without it elsewhere.
 * @param {boolean} [props.totalLoading] - true while staffTotal itself is loading
 * @param {string} [props.accent] - accent modifier passed through to MetricCard
 */
const VacationMetricCard = ({ staffTotal, totalLoading = false, accent = 'periwinkle' }) => {
  const { t } = useTranslation('dashboard');
  const { fetchVacationsForDay } = useVacation();
  const { currentCompany } = useAuth();

  const [todayCount, setTodayCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchTodayVacations = async () => {
      try {
        if (!currentCompany?.id) {
          if (!cancelled) setLoading(false);
          return;
        }

        setLoading(true);
        const today = new Date();
        const vacations = await fetchVacationsForDay(today);

        if (!cancelled) {
          setTodayCount(vacations ? vacations.length : 0);
        }
      } catch (err) {
        console.error('Error fetching today\'s vacations:', err);
        if (!cancelled) {
          setTodayCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTodayVacations();

    return () => {
      cancelled = true;
    };
  }, [currentCompany, fetchVacationsForDay]);

  const sublabel =
    staffTotal !== undefined
      ? `${t('ofTotal')} ${totalLoading ? '…' : staffTotal}`
      : undefined;

  return (
    <MetricCard
      icon={<RiSunLine />}
      label={t('onVacationToday')}
      value={todayCount}
      sublabel={sublabel}
      loading={loading}
      accent={accent}
    />
  );
};

export default VacationMetricCard;